import { AUTO_MODE_LISTS } from "./copy.js";
import { AutoModeDefaultsNotice, ChoiceRow, ResourceList, ToggleControl } from "../controls.jsx";
import { GroupHeader, Interaction, Mechanics, OnEndpoints } from "../textRoles.jsx";

export function PermitAutoMode({ controls, updateControls, canManage }) {
    return (
        <>
            <GroupHeader title="Permit auto mode" orientation="Whether a user may choose Auto, where a classifier approves actions instead of asking." keys={["permissions.disableAutoMode"]} />
            <ToggleControl disabled={!canManage} checked={controls.autoModeAllowed} onChange={(checked) => updateControls((value) => ({ ...value, autoModeAllowed: checked, defaultMode: !checked && value.defaultMode === "auto" ? "default" : value.defaultMode }))} label="Permit auto mode" />
            <OnEndpoints written={!controls.autoModeAllowed}>{controls.autoModeAllowed ? "No Auto restriction is written. A user may choose Auto where their account, provider, model, and Claude Code version support it." : "Auto is refused in every managed session, including as a starting mode."}</OnEndpoints>
            {!controls.autoModeAllowed ? <Interaction scope="Permit auto mode">The classifier controls are hidden while Auto is off. Classifier settings already saved stay dormant, and return unchanged if Auto is permitted again.</Interaction> : null}
        </>
    );
}

export function AutoModeEnvironment({ controls, updateControls, canManage }) {
    return (
        <>
            <GroupHeader title="Describe your environment" orientation="The trusted context the Auto mode classifier uses to judge actions." keys={["autoMode.environment"]} />
            <Mechanics>Describe which infrastructure is internal or trusted and which targets are sensitive. These are facts for Auto's classifier, not tool permissions or command patterns.</Mechanics>
            <ResourceList
                disabled={!canManage}
                items={controls.autoMode.environment}
                onRemove={(item) => updateControls((value) => ({ ...value, autoMode: { ...value.autoMode, environment: value.autoMode.environment.filter((entry) => entry !== item) } }))}
                onAdd={(item) => updateControls((value) => ({ ...value, autoMode: { ...value.autoMode, environment: item === "$defaults" || value.autoMode.environment.includes(item) ? value.autoMode.environment : [...value.autoMode.environment, item] } }))}
                addLabel={AUTO_MODE_LISTS.environment.addLabel}
                inputLabel={AUTO_MODE_LISTS.environment.inputLabel}
                placeholder={AUTO_MODE_LISTS.environment.placeholder}
                help={AUTO_MODE_LISTS.environment.help}
                example={AUTO_MODE_LISTS.environment.example}
            />
            <AutoModeDefaultsNotice
                disabled={!canManage}
                hasEntries={controls.autoMode.environment.length > 0}
                includesDefaults={controls.autoModeDefaults.environment}
                setting="environment context"
                onRestore={() => updateControls((value) => ({ ...value, autoModeDefaults: { ...value.autoModeDefaults, environment: true } }))}
            />
        </>
    );
}

export function ClassifierDecisions({ controls, updateControls, canManage, ui, setUi }) {
    const autoRuleKind = ui.autoRuleKind;
    return (
        <>
            <GroupHeader title="Auto mode classifier rules" orientation="How Auto reviews actions that tool permission rules have not resolved." keys={["autoMode.hard_deny", "autoMode.soft_deny", "autoMode.allow"]} />
            <Mechanics>These entries guide Auto's classifier. Use a managed Block rule for tool permissions outside Auto.</Mechanics>
            <ChoiceRow disabled={!canManage} label="Rule behavior" value={autoRuleKind} onChange={(value) => setUi((current) => ({ ...current, autoRuleKind: value }))} options={["hardDeny", "softDeny", "allow"].map((value) => ({ value, label: AUTO_MODE_LISTS[value].label }))} />
            <ResourceList
                disabled={!canManage}
                items={controls.autoMode[autoRuleKind]}
                onRemove={(item) => updateControls((value) => ({ ...value, autoMode: { ...value.autoMode, [autoRuleKind]: value.autoMode[autoRuleKind].filter((entry) => entry !== item) } }))}
                onAdd={(item) => updateControls((value) => ({ ...value, autoMode: { ...value.autoMode, [autoRuleKind]: item === "$defaults" || value.autoMode[autoRuleKind].includes(item) ? value.autoMode[autoRuleKind] : [...value.autoMode[autoRuleKind], item] } }))}
                addLabel={AUTO_MODE_LISTS[autoRuleKind].addLabel}
                inputLabel={AUTO_MODE_LISTS[autoRuleKind].inputLabel}
                placeholder={AUTO_MODE_LISTS[autoRuleKind].placeholder}
                help={AUTO_MODE_LISTS[autoRuleKind].help}
                example={AUTO_MODE_LISTS[autoRuleKind].example}
            />
            {autoRuleKind === "softDeny" ? <Interaction scope="Allow exceptions">A personal Allow entry can override this managed soft block. The managed-only permission-rule switch does not change how classifier entries merge.</Interaction> : null}
            <AutoModeDefaultsNotice
                disabled={!canManage}
                hasEntries={controls.autoMode[autoRuleKind].length > 0}
                includesDefaults={controls.autoModeDefaults[autoRuleKind]}
                setting={`${AUTO_MODE_LISTS[autoRuleKind].label.toLowerCase()} rules`}
                onRestore={() => updateControls((value) => ({ ...value, autoModeDefaults: { ...value.autoModeDefaults, [autoRuleKind]: true } }))}
            />
        </>
    );
}

export function ReviewEveryShellCommand({ controls, updateControls, canManage }) {
    return (
        <>
            <GroupHeader title="Review every shell command" orientation="Whether the Auto mode classifier also reviews shell commands that a permission rule already allows." keys={["autoMode.classifyAllShell"]} />
            <ToggleControl disabled={!canManage} checked={controls.autoMode.classifyAllShell} onChange={(checked) => updateControls((value) => ({ ...value, autoMode: { ...value.autoMode, classifyAllShell: checked } }))} label="Review every shell command in auto mode" description="Makes the classifier review Bash and PowerShell commands even when a permission rule would normally pre-approve them. This adds classifier calls and can increase command latency." />
        </>
    );
}
